import React, { useEffect } from 'react';
import { useFormik, FormikValues } from 'formik';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { Constants, Validators } from 'core';
import { Profil, RequestError, ServiceType, UserCodeError } from 'types';
import { path } from 'ramda';

import Button from 'components/molecules/button';
import Input from 'components/organisms/input';
import history from 'app-router/history';
import FormError from 'components/molecules/form-error';
import { ClearStateSliceActions } from 'common/config/store';
import { useIntl } from 'common/config/i18n';
import InputSelect from 'components/organisms/select';

const UpLabel = styled.div`
  font-size: ${path(['theme', 'font', 'size', 'body'])};
  font-weight: bold;
`;
const ButtonWrapper = styled.div`
  display: flex;
  width: 100%;
`;
const WrapperForm = styled.header`
  margin-bottom: 20px;
`;
const WrapperInput = styled.div`
  margin-top: 20px;
`;
const EmailModifyInfo = styled.div`
  margin-top: 10px;
  width: 300px;
  font-size: 10px;
`;

interface Props {
  onUpdate: (values: FormikValues) => void;
  profils: Profil[];
  serviceTypes: ServiceType[];
  title?: string;
  id: string;
  userError?: RequestError;
  userStatus?: 'loading' | 'error' | 'success';
  userInfoToModifyStatus?: 'loading' | 'error' | 'success';
  isUpdatingCurrentUser?: boolean;
  userCodeError?: UserCodeError;
}

interface FormValues {
  userName?: string;
  firstName?: string;
  profileID?: number;
  trainTypeIDs?: number[];
  groupName?: string;
  lastName?: string;
  email?: string;
}

const Form = (props: Props & FormValues) => {
  const { t } = useIntl();
  const dispatch = useDispatch();

  const initialValues = {
    firstName: props.firstName || '',
    userName: props.userName || '',
    profileID: props.profileID || undefined,
    trainTypeIDs: props.trainTypeIDs || (props.serviceTypes.length === 1 ? [props.serviceTypes[0].id] : undefined),
    groupName: props.groupName || '',
    lastName: props.lastName || '',
    email: props.email || '',
  };
  const formik = useFormik({
    initialValues,
    validate: (values) => Validators.modifyUser(values),
    onSubmit: (values) => {
      props.onUpdate({ ...values, id: props?.id });
    },
  });

  useEffect(() => {
    dispatch(ClearStateSliceActions.clearStateSlice({ sliceReducer: 'User' }));
  }, [dispatch]);

  const selectProfilsData = [
    {
      id: 'profils',
      list:
        props?.profils?.map((profil) => ({
          id: profil.id,
          name: profil.name,
        })) || [],
    },
  ];

  const selectServiceTypesData = [
    {
      id: 'serviceTypes',
      list:
        props?.serviceTypes?.map((serviceType) => ({
          id: serviceType.id,
          name: serviceType.serviceTypeName,
        })) || [],
    },
  ];

  return (
    <>
      {props.userInfoToModifyStatus === 'success' && (
        <form onSubmit={formik.handleSubmit}>
          <WrapperForm>
            <Input
              width="300px"
              upLabel={t('form', 'FORM_USER_NAME')}
              name="lastName"
              onChange={formik.handleChange}
              value={formik.values.lastName}
            />
            <FormError formik={formik} fieldName="lastName" />

            <WrapperInput>
              <Input
                width="300px"
                upLabel={t('form', 'FORM_USER_SURNAME')}
                name="firstName"
                onChange={formik.handleChange}
                value={formik.values.firstName}
              />
            </WrapperInput>
            <FormError formik={formik} fieldName="firstName" />

            <WrapperInput>
              <Input
                width="300px"
                upLabel={t('form', 'FORM_USERNAME')}
                name="userName"
                onChange={formik.handleChange}
                value={formik.values.userName}
              />
            </WrapperInput>
            <FormError formik={formik} fieldName="userName" />

            <WrapperInput>
              <Input
                width="300px"
                upLabel={t('form', 'FORM_EMAIL')}
                type="email"
                name="email"
                onChange={formik.handleChange}
                value={formik.values.email}
              />
            </WrapperInput>
            <FormError formik={formik} fieldName="email" />

            <WrapperInput>
              <UpLabel>{t('form', 'FORM_USER_PROFIL')}</UpLabel>
              <InputSelect
                onChange={(id) => formik.setFieldValue('profileID', id)}
                defaultValue={formik.values.profileID}
                data={selectProfilsData}
                disabled={props.isUpdatingCurrentUser}
                style={{ width: '300px', borderRadius: '5px' }}
              />
            </WrapperInput>
            <FormError formik={formik} fieldName="profileID" />

            <WrapperInput>
              <UpLabel>{t('form', 'FORM_USER_CIRCULATION_TYPE')}</UpLabel>
              <InputSelect
                mode="multiple"
                onChange={(trainTypeIDs) => formik.setFieldValue('trainTypeIDs', trainTypeIDs)}
                defaultValue={formik.values.trainTypeIDs}
                data={selectServiceTypesData}
                style={{ width: '300px', borderRadius: '5px' }}
              />
            </WrapperInput>
            <FormError formik={formik} fieldName="trainTypeIDs" />

            <WrapperInput>
              <Input
                width="300px"
                upLabel={t('form', 'FORM_USER_GROUP')}
                name="groupName"
                onChange={formik.handleChange}
                value={formik.values.groupName}
              />
            </WrapperInput>
            <FormError formik={formik} fieldName="groupName" />

            <EmailModifyInfo>{t('form', 'EMAIL_INFO')}</EmailModifyInfo>
          </WrapperForm>

          {props?.userError === Constants.ALREADY_EXISTED_PROBLEM_CODE &&
            props?.userCodeError?.includes(Constants.USER_EMAIL_ALREADY_EXISTED_CODE_ERROR) && (
              <FormError message={t('form', 'FORM_USER_EMAIL_ALLREADY_EXIST')} />
            )}
          {props?.userError === Constants.ALREADY_EXISTED_PROBLEM_CODE &&
            props?.userCodeError?.includes(Constants.USER_USERNAME_ALREADY_EXISTED_CODE_ERROR) && (
              <FormError message={t('form', 'FORM_USER_USERNAME_ALLREADY_EXIST')} />
            )}
          {props?.userError && props?.userError !== Constants.ALREADY_EXISTED_PROBLEM_CODE && (
            <FormError message={t('form', 'FORM_UNKNOWN_ERROR')} />
          )}

          <ButtonWrapper>
            <Button
              height="35px"
              width="100%"
              borderRadius="20px"
              disabled={
                !!(formik.errors.userName && formik.touched.userName) ||
                !!(formik.errors.firstName && formik.touched.firstName) ||
                !!(formik.errors.profileID && formik.touched.profileID) ||
                !!(formik.errors.trainTypeIDs && formik.touched.trainTypeIDs) ||
                !!(formik.errors.groupName && formik.touched.groupName) ||
                !!(formik.errors.lastName && formik.touched.lastName) ||
                !!(formik.errors.email && formik.touched.email)
              }
              loading={props.userStatus === 'loading'}
              htmlType="submit"
            >
              {t('Buttons', 'SAVE_BUTTON')}
            </Button>
            <Button
              height="35px"
              margin=" 0 0 0 2px"
              width="100%"
              borderRadius="20px"
              onClick={() => {
                history.push('/users');
              }}
            >
              {t('Buttons', 'CANCEL_BUTTON')}
            </Button>
          </ButtonWrapper>
        </form>
      )}
    </>
  );
};
export default Form;
